import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { 
  Camera, 
  Upload, 
  Barcode, 
  X, 
  Check, 
  AlertCircle,
  ChevronRight,
  Flame,
  Beef,
  Wheat,
  Droplets,
  Leaf,
  Plus,
  Minus,
  Target,
  TrendingUp,
  Sparkles,
  RefreshCw,
  History,
  ScanLine,
  ImageIcon,
  Loader2,
  Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function FoodScannerPage() {
  const { user, token } = useAuth();
  const [scanMode, setScanMode] = useState(null); // 'barcode', 'camera', 'upload'
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [portionMultiplier, setPortionMultiplier] = useState(1);
  const [recentScans, setRecentScans] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState('lunch');
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch recent scans on mount
  useEffect(() => {
    fetchRecentScans();
  }, []);

  const fetchRecentScans = async () => {
    try {
      const response = await axios.get(`${API}/food-scanner/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentScans(response.data.recent_scans || []);
    } catch (error) {
      console.error('Error fetching scan history:', error);
    }
  };

  // Camera handling
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } } 
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      toast.error('Unable to access camera. Please check permissions.');
      console.error('Camera error:', error);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      stopCamera();
      analyzeImage(imageData.split(',')[1]);
    }
  };

  useEffect(() => {
    if (scanMode === 'camera') {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [scanMode]);

  // Barcode scanning
  const handleBarcodeScan = async () => {
    if (!barcodeInput.trim()) {
      toast.error('Please enter a barcode');
      return;
    }
    
    setIsScanning(true);
    try {
      const response = await axios.post(
        `${API}/food-scanner/barcode`,
        { barcode: barcodeInput.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScanResult(response.data);
      setPortionMultiplier(1);
      toast.success('Food identified!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Product not found');
    } finally {
      setIsScanning(false);
    }
  };

  // Image analysis
  const analyzeImage = async (base64Image) => {
    setIsScanning(true);
    try {
      const response = await axios.post(
        `${API}/food-scanner/image`,
        { image_base64: base64Image },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScanResult(response.data);
      setPortionMultiplier(1);
      toast.success('Food analyzed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to analyze image');
      setCapturedImage(null);
    } finally {
      setIsScanning(false);
    }
  };

  // File upload handling
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result;
      setCapturedImage(base64);
      analyzeImage(base64.split(',')[1]);
    };
    reader.readAsDataURL(file);
  };

  // Portion adjustment
  const adjustPortion = async (multiplier) => {
    if (!scanResult?.food_data) return;
    
    setPortionMultiplier(multiplier);
    
    try {
      const response = await axios.post(
        `${API}/food-scanner/adjust-portion`,
        {
          food_name: scanResult.food_data.food_name,
          original_calories: scanResult.food_data.calories / portionMultiplier,
          original_protein: scanResult.food_data.protein_g / portionMultiplier,
          original_carbs: scanResult.food_data.carbs_g / portionMultiplier,
          original_fats: scanResult.food_data.fats_g / portionMultiplier,
          original_fiber: scanResult.food_data.fiber_g / portionMultiplier,
          multiplier: multiplier
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setScanResult(prev => ({
        ...prev,
        food_data: {
          ...prev.food_data,
          calories: response.data.adjusted_food.calories,
          protein_g: response.data.adjusted_food.protein_g,
          carbs_g: response.data.adjusted_food.carbs_g,
          fats_g: response.data.adjusted_food.fats_g,
          fiber_g: response.data.adjusted_food.fiber_g
        },
        coaching: response.data.coaching
      }));
    } catch (error) {
      console.error('Error adjusting portion:', error);
    }
  };

  // Add to food log
  const addToLog = async () => {
    if (!scanResult?.food_data) return;
    
    const today = new Date().toISOString().split('T')[0];
    
    try {
      await axios.post(
        `${API}/food-scanner/add-to-log`,
        {
          date: today,
          meal_type: selectedMeal,
          food_name: scanResult.food_data.food_name,
          portion: scanResult.food_data.portion,
          calories: scanResult.food_data.calories,
          protein_g: scanResult.food_data.protein_g,
          carbs_g: scanResult.food_data.carbs_g,
          fats_g: scanResult.food_data.fats_g,
          fiber_g: scanResult.food_data.fiber_g,
          coaching_insight: scanResult.coaching?.primary_insight
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Added to ${selectedMeal}!`);
      setShowAddModal(false);
      fetchRecentScans();
    } catch (error) {
      toast.error('Failed to add to food log');
    }
  };

  // Reset scanner
  const resetScanner = () => {
    setScanResult(null);
    setCapturedImage(null);
    setBarcodeInput('');
    setPortionMultiplier(1);
    setScanMode(null);
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-red-500 flex items-center justify-center">
            <ScanLine className="w-5 h-5" />
          </div>
          Food Scanner
        </h1>
        <p className="text-gray-400 mt-1">Scan → Analyze → Coach → Improve</p>
      </div>

      {/* Main Content */}
      {!scanResult ? (
        <>
          {/* Scan Options */}
          {!scanMode && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Barcode Scanner */}
                <button
                  data-testid="scan-barcode-btn"
                  onClick={() => setScanMode('barcode')}
                  className="group p-6 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-primary/50 transition-all text-left"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Barcode className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-1">Scan Barcode</h3>
                  <p className="text-sm text-gray-400">Enter barcode from packaged food</p>
                </button>

                {/* Camera */}
                <button
                  data-testid="scan-camera-btn"
                  onClick={() => setScanMode('camera')}
                  className="group p-6 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-primary/50 transition-all text-left"
                >
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Camera className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-1">Take Photo</h3>
                  <p className="text-sm text-gray-400">AI analyzes your meal instantly</p>
                </button>

                {/* Upload */}
                <button
                  data-testid="scan-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  className="group p-6 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-primary/50 transition-all text-left"
                >
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Upload className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-1">Upload Image</h3>
                  <p className="text-sm text-gray-400">Choose from your gallery</p>
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />

              {/* Recent Scans */}
              {recentScans.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <History className="w-5 h-5 text-gray-400" />
                    Recent Scans
                  </h3>
                  <div className="space-y-2">
                    {recentScans.slice(0, 5).map((food, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setScanResult({
                            source: 'history',
                            food_data: food,
                            coaching: null
                          });
                        }}
                        className="w-full p-4 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-colors text-left flex items-center justify-between"
                      >
                        <div>
                          <p className="font-medium">{food.food_name}</p>
                          <p className="text-sm text-gray-400">{food.portion} • {food.calories} cal</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Barcode Input */}
          {scanMode === 'barcode' && (
            <div className="space-y-4">
              <button
                onClick={() => setScanMode(null)}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
                Back
              </button>
              
              <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
                <div className="flex items-center gap-3 mb-4">
                  <Barcode className="w-6 h-6 text-blue-400" />
                  <h3 className="text-lg font-semibold">Enter Barcode</h3>
                </div>
                
                <input
                  data-testid="barcode-input"
                  type="text"
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleBarcodeScan()}
                  placeholder="Enter barcode number..."
                  className="w-full p-4 rounded-xl bg-zinc-800 border border-zinc-700 focus:border-primary outline-none text-lg tracking-wider"
                  autoFocus
                />
                
                <Button
                  data-testid="scan-barcode-submit"
                  onClick={handleBarcodeScan}
                  disabled={isScanning}
                  className="w-full mt-4 bg-primary hover:bg-primary/90"
                >
                  {isScanning ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <ScanLine className="w-4 h-4 mr-2" />
                      Look Up Food
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Camera View */}
          {scanMode === 'camera' && !capturedImage && (
            <div className="space-y-4">
              <button
                onClick={() => {
                  stopCamera();
                  setScanMode(null);
                }}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
                Back
              </button>
              
              <div className="relative rounded-2xl overflow-hidden bg-black aspect-video">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
                
                {/* Capture overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-64 h-64 border-2 border-white/30 rounded-2xl" />
                </div>
                
                <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                  <Button
                    data-testid="capture-photo-btn"
                    onClick={capturePhoto}
                    className="bg-white text-black hover:bg-gray-200 rounded-full w-16 h-16"
                  >
                    <Camera className="w-6 h-6" />
                  </Button>
                </div>
              </div>
              
              <p className="text-center text-gray-400 text-sm">
                Position your meal in the frame and tap the capture button
              </p>
            </div>
          )}

          {/* Loading State */}
          {isScanning && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                <p className="text-lg font-medium">Analyzing your food...</p>
                <p className="text-gray-400 text-sm mt-1">AI is identifying nutrients</p>
              </div>
            </div>
          )}

          <canvas ref={canvasRef} className="hidden" />
        </>
      ) : (
        /* Scan Results */
        <div className="space-y-6">
          {/* Back Button */}
          <button
            onClick={resetScanner}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Scan Another
          </button>

          {/* Food Image (if available) */}
          {capturedImage && (
            <div className="rounded-2xl overflow-hidden">
              <img src={capturedImage} alt="Scanned food" className="w-full h-48 object-cover" />
            </div>
          )}

          {/* Food Info Card */}
          <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold">{scanResult.food_data?.food_name}</h2>
                <p className="text-gray-400">{scanResult.food_data?.portion}</p>
                {scanResult.food_data?.brand && (
                  <p className="text-sm text-gray-500">{scanResult.food_data.brand}</p>
                )}
              </div>
              {scanResult.food_data?.confidence && (
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  scanResult.food_data.confidence === 'high' ? 'bg-green-500/20 text-green-400' :
                  scanResult.food_data.confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {scanResult.food_data.confidence} confidence
                </span>
              )}
            </div>

            {/* Macros Grid */}
            <div className="grid grid-cols-5 gap-3 mb-6">
              <div className="text-center p-3 rounded-xl bg-zinc-800">
                <Flame className="w-5 h-5 text-orange-400 mx-auto mb-1" />
                <p className="text-lg font-bold">{Math.round(scanResult.food_data?.calories || 0)}</p>
                <p className="text-xs text-gray-400">Calories</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-zinc-800">
                <Beef className="w-5 h-5 text-red-400 mx-auto mb-1" />
                <p className="text-lg font-bold">{Math.round(scanResult.food_data?.protein_g || 0)}g</p>
                <p className="text-xs text-gray-400">Protein</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-zinc-800">
                <Wheat className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                <p className="text-lg font-bold">{Math.round(scanResult.food_data?.carbs_g || 0)}g</p>
                <p className="text-xs text-gray-400">Carbs</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-zinc-800">
                <Droplets className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                <p className="text-lg font-bold">{Math.round(scanResult.food_data?.fats_g || 0)}g</p>
                <p className="text-xs text-gray-400">Fats</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-zinc-800">
                <Leaf className="w-5 h-5 text-green-400 mx-auto mb-1" />
                <p className="text-lg font-bold">{Math.round(scanResult.food_data?.fiber_g || 0)}g</p>
                <p className="text-xs text-gray-400">Fiber</p>
              </div>
            </div>

            {/* Portion Adjustment */}
            <div className="mb-6">
              <p className="text-sm text-gray-400 mb-2">Adjust Portion</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => adjustPortion(Math.max(0.25, portionMultiplier - 0.25))}
                  className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <div className="flex-1 text-center">
                  <span className="text-2xl font-bold">{portionMultiplier}x</span>
                  <span className="text-gray-400 ml-2">serving</span>
                </div>
                <button
                  onClick={() => adjustPortion(portionMultiplier + 0.25)}
                  className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex justify-center gap-2 mt-2">
                {[0.5, 1, 1.5, 2].map((mult) => (
                  <button
                    key={mult}
                    onClick={() => adjustPortion(mult)}
                    className={`px-3 py-1 rounded-full text-sm ${
                      portionMultiplier === mult
                        ? 'bg-primary text-white'
                        : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'
                    }`}
                  >
                    {mult}x
                  </button>
                ))}
              </div>
            </div>

            {/* Key Metrics */}
            {scanResult.coaching && (
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="p-3 rounded-xl bg-gradient-to-r from-primary/10 to-transparent border border-primary/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="w-4 h-4 text-primary" />
                    <span className="text-xs text-gray-400">Protein/Cal Ratio</span>
                  </div>
                  <p className="text-lg font-bold">{scanResult.coaching.protein_cal_ratio}%</p>
                </div>
                <div className="p-3 rounded-xl bg-gradient-to-r from-green-500/10 to-transparent border border-green-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    <span className="text-xs text-gray-400">Nutrient Density</span>
                  </div>
                  <p className="text-lg font-bold">{scanResult.coaching.nutrient_density_score}</p>
                </div>
              </div>
            )}

            {/* Ingredients */}
            {scanResult.food_data?.ingredients && (
              <div className="mb-6">
                <p className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Ingredients
                </p>
                <p className="text-sm text-gray-300">
                  {Array.isArray(scanResult.food_data.ingredients) 
                    ? scanResult.food_data.ingredients.join(', ')
                    : scanResult.food_data.ingredients}
                </p>
              </div>
            )}
          </div>

          {/* Coaching Insight Card */}
          {scanResult.coaching && (
            <div className="p-6 rounded-2xl bg-gradient-to-br from-primary/20 to-zinc-900 border border-primary/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Coach Insight</h3>
                  <p className="text-xs text-gray-400">Based on your {scanResult.user_goal?.replace('_', ' ')} goal</p>
                </div>
              </div>
              
              <p className="text-lg mb-4">{scanResult.coaching.primary_insight}</p>
              
              {/* Additional coaching points */}
              {scanResult.coaching.coaching_points?.length > 1 && (
                <div className="space-y-2 mb-4">
                  {scanResult.coaching.coaching_points.slice(1).map((point, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Suggestions */}
              {scanResult.coaching.suggestions?.length > 0 && (
                <div className="space-y-2 mb-4">
                  {scanResult.coaching.suggestions.map((suggestion, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm text-amber-300">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{suggestion}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Warnings */}
              {scanResult.coaching.warnings?.length > 0 && (
                <div className="space-y-2">
                  {scanResult.coaching.warnings.map((warning, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm text-red-300">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{warning}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Daily Progress */}
          {scanResult.daily_progress && (
            <div className="p-4 rounded-2xl bg-zinc-900 border border-zinc-800">
              <h4 className="text-sm text-gray-400 mb-3">Today's Remaining</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-2xl font-bold text-primary">
                    {Math.max(0, Math.round(scanResult.daily_progress.remaining_calories))}
                  </p>
                  <p className="text-xs text-gray-400">calories left</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">
                    {Math.max(0, Math.round(scanResult.daily_progress.remaining_protein))}g
                  </p>
                  <p className="text-xs text-gray-400">protein left</p>
                </div>
              </div>
            </div>
          )}

          {/* Add to Log Button */}
          <Button
            data-testid="add-to-log-btn"
            onClick={() => setShowAddModal(true)}
            className="w-full bg-primary hover:bg-primary/90 py-6 text-lg"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add to Food Log
          </Button>
        </div>
      )}

      {/* Add to Meal Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Add to Meal</h3>
            
            <div className="space-y-2 mb-6">
              {['breakfast', 'lunch', 'dinner', 'snack'].map((meal) => (
                <button
                  key={meal}
                  onClick={() => setSelectedMeal(meal)}
                  className={`w-full p-4 rounded-xl text-left capitalize ${
                    selectedMeal === meal
                      ? 'bg-primary text-white'
                      : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
                  }`}
                >
                  {meal}
                </button>
              ))}
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAddModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-add-btn"
                onClick={addToLog}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
